import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { API } from '../../config/api';
import {
  Star, Search, RefreshCw, Trash2, Loader2, CheckCircle, XCircle,
  Music, Video, Mic2, Users, Youtube, Calendar, Megaphone, Radio,
  ChevronDown, ChevronRight, Eye, EyeOff, Plus
} from 'lucide-react';

const FEATURED_TYPES = [
  { value: 'top_song', label: 'Top Song', icon: Music, searchType: 'track', color: 'text-emerald-400' },
  { value: 'top_video', label: 'Top Video', icon: Video, searchType: 'video', color: 'text-blue-400' },
  { value: 'featured_artist', label: 'Featured Artist', icon: Users, searchType: 'user', color: 'text-purple-400' },
  { value: 'featured_track', label: 'Featured Track', icon: Music, searchType: 'track', color: 'text-green-400' },
  { value: 'featured_video', label: 'Featured Video', icon: Video, searchType: 'video', color: 'text-cyan-400' },
  { value: 'featured_interview', label: 'Featured Interview', icon: Mic2, searchType: 'video', color: 'text-pink-400' },
  { value: 'youtube_embed', label: 'YouTube Embed', icon: Youtube, searchType: 'youtube', color: 'text-red-400' },
  { value: 'community_spotlight', label: 'Community Spotlight', icon: Radio, searchType: 'user', color: 'text-amber-400' },
  { value: 'event_of_week', label: 'Event of the Week', icon: Calendar, searchType: 'manual', color: 'text-orange-400' },
  { value: 'new_release', label: 'New Release', icon: Megaphone, searchType: 'track', color: 'text-teal-400' },
  { value: 'announcement', label: 'Announcement', icon: Megaphone, searchType: 'manual', color: 'text-yellow-400' },
];

const getTypeConfig = (type) => FEATURED_TYPES.find(t => t.value === type) || { label: type, icon: Star, color: 'text-gray-400' };

export default function FeaturedContentManager({ getAuthHeaders }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedType, setExpandedType] = useState(null);
  const [formType, setFormType] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/featured/all`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setItems(data.featured || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deactivate = async (identifier) => {
    try {
      const res = await fetch(`${API}/admin/featured/${identifier}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (res.ok) { toast.success('Featured content deactivated'); fetchAll(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch (e) { toast.error('Failed to deactivate'); }
  };

  // Group items by type
  const grouped = {};
  for (const t of FEATURED_TYPES) {
    grouped[t.value] = items.filter(i => i.type === t.value);
  }

  return (
    <Card className="p-6 space-y-6" data-testid="featured-content-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Star className="w-6 h-6 text-yellow-500" />
          <div>
            <h2 className="text-xl font-bold">Featured Content Manager</h2>
            <p className="text-sm text-muted-foreground">Manage homepage spotlight sections</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-featured">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="space-y-2">
          {FEATURED_TYPES.map(typeConf => {
            const typeItems = grouped[typeConf.value] || [];
            const activeItem = typeItems.find(i => i.active);
            const Icon = typeConf.icon;
            const isExpanded = expandedType === typeConf.value;

            return (
              <div key={typeConf.value} className="border rounded-lg overflow-hidden" data-testid={`featured-type-${typeConf.value}`}>
                {/* Type Header */}
                <button
                  className="w-full flex items-center gap-3 p-4 hover:bg-muted/30 transition-colors text-left"
                  onClick={() => setExpandedType(isExpanded ? null : typeConf.value)}
                  data-testid={`toggle-type-${typeConf.value}`}
                >
                  <div className={`p-2 rounded-lg bg-muted/30 ${typeConf.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{typeConf.label}</p>
                    {activeItem ? (
                      <p className="text-xs text-muted-foreground truncate">
                        Active: {activeItem.title || activeItem.content_id || 'Untitled'}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground/50">No active content</p>
                    )}
                  </div>
                  <Badge variant={activeItem ? 'default' : 'outline'} className={activeItem ? 'bg-green-600 text-foreground text-xs' : 'text-xs'}>
                    {activeItem ? 'Active' : 'Empty'}
                  </Badge>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t px-4 pb-4 pt-3 space-y-3 bg-muted/20">
                    {/* Active Item Display */}
                    {activeItem && (
                      <ActiveItemCard item={activeItem} onDeactivate={deactivate} />
                    )}

                    {/* History */}
                    {typeItems.filter(i => !i.active).length > 0 && (
                      <details className="text-sm">
                        <summary className="text-muted-foreground cursor-pointer hover:text-foreground text-xs">
                          {typeItems.filter(i => !i.active).length} previous item(s)
                        </summary>
                        <div className="mt-2 space-y-1">
                          {typeItems.filter(i => !i.active).slice(0, 5).map(item => (
                            <div key={item.featured_id} className="flex items-center gap-2 p-2 rounded bg-muted/20 text-xs text-muted-foreground">
                              <EyeOff className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{item.title || item.content_id || 'Untitled'}</span>
                              <span className="ml-auto whitespace-nowrap">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Set New Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setFormType(formType === typeConf.value ? null : typeConf.value)}
                      data-testid={`set-new-${typeConf.value}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {activeItem ? 'Replace' : 'Set'} {typeConf.label}
                    </Button>

                    {/* Inline Form */}
                    {formType === typeConf.value && (
                      <SetFeaturedForm
                        typeConf={typeConf}
                        getAuthHeaders={getAuthHeaders}
                        onSuccess={() => { setFormType(null); fetchAll(); }}
                        onCancel={() => setFormType(null)}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function ActiveItemCard({ item, onDeactivate }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-green-500/20 bg-green-500/5" data-testid={`active-item-${item.featured_id}`}>
      {item.thumbnail_url && (
        <img src={item.thumbnail_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Eye className="w-3 h-3 text-green-500" />
          <span className="text-xs font-medium text-green-500">LIVE ON HOMEPAGE</span>
        </div>
        <p className="font-semibold text-sm truncate">{item.title || 'Untitled'}</p>
        {item.description && <p className="text-xs text-muted-foreground truncate">{item.description}</p>}
        <div className="flex flex-wrap gap-2 mt-1">
          {item.badge_text && <Badge variant="outline" className="text-xs">{item.badge_text}</Badge>}
          {item.content_type && <span className="text-xs text-muted-foreground">Type: {item.content_type}</span>}
          {item.artist_id && <span className="text-xs text-muted-foreground">Artist: {item.artist_id}</span>}
          {item.track_id && <span className="text-xs text-muted-foreground">Track: {item.track_id}</span>}
          {item.video_id && <span className="text-xs text-muted-foreground">Video: {item.video_id}</span>}
          {item.youtube_url && <span className="text-xs text-muted-foreground truncate">YT: {item.youtube_url}</span>}
        </div>
        <p className="text-xs text-muted-foreground mt-1">Set: {item.created_at ? new Date(item.created_at).toLocaleString() : 'Unknown'}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0"
        onClick={() => onDeactivate(item.featured_id)}
        data-testid={`deactivate-${item.featured_id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function SetFeaturedForm({ typeConf, getAuthHeaders, onSuccess, onCancel }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [badgeText, setBadgeText] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }

    setSubmitting(true);
    try {
      const body = {
        type: typeConf.value,
        title: title.trim(),
        description: description.trim() || undefined,
        badge_text: badgeText.trim() || undefined,
        cta_text: ctaText.trim() || undefined,
        thumbnail_url: thumbnailUrl.trim() || undefined,
        external_url: externalUrl.trim() || undefined,
      };

      // Attach content references
      if (selectedContent) {
        if (typeConf.searchType === 'track') {
          body.content_id = selectedContent.track_id;
          body.content_type = 'track';
          body.track_id = selectedContent.track_id;
          body.artist_id = selectedContent.artist_id;
          if (!thumbnailUrl) body.thumbnail_url = selectedContent.cover_art_url || selectedContent.cover_url;
        } else if (typeConf.searchType === 'video') {
          body.content_id = selectedContent.video_id;
          body.content_type = 'video';
          body.video_id = selectedContent.video_id;
          body.artist_id = selectedContent.artist_id;
          if (!thumbnailUrl) body.thumbnail_url = selectedContent.thumbnail_url;
        } else if (typeConf.searchType === 'user') {
          body.content_id = selectedContent.user_id;
          body.content_type = 'user';
          if (!thumbnailUrl) body.thumbnail_url = selectedContent.avatar_url || selectedContent.picture;
        }
      }

      // YouTube
      if (typeConf.searchType === 'youtube' && youtubeUrl) {
        body.youtube_url = youtubeUrl;
        const ytMatch = youtubeUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?/]+)/);
        if (ytMatch) {
          body.youtube_video_id = ytMatch[1];
          body.youtube_embed_url = `https://www.youtube.com/embed/${ytMatch[1]}`;
          if (!thumbnailUrl) body.thumbnail_url = `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
        }
      }

      const res = await fetch(`${API}/admin/featured/set`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success(`${typeConf.label} updated on homepage!`);
        onSuccess();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to set featured content');
      }
    } catch (e) {
      toast.error('Error setting featured content');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-muted/20" data-testid={`form-${typeConf.value}`}>
      <h4 className="font-semibold text-sm flex items-center gap-2">
        <Plus className="w-4 h-4" /> Set {typeConf.label}
      </h4>

      {/* Content Search (if applicable) */}
      {typeConf.searchType !== 'manual' && typeConf.searchType !== 'youtube' && (
        <ContentSearch
          searchType={typeConf.searchType}
          getAuthHeaders={getAuthHeaders}
          selected={selectedContent}
          onSelect={(item) => {
            setSelectedContent(item);
            if (!title) {
              setTitle(item.title || item.name || item.stage_name || '');
            }
          }}
        />
      )}

      {/* YouTube URL */}
      {typeConf.searchType === 'youtube' && (
        <Input
          placeholder="YouTube URL (e.g. https://youtube.com/watch?v=...)"
          value={youtubeUrl}
          onChange={e => setYoutubeUrl(e.target.value)}
          data-testid="youtube-url-input"
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input placeholder="Title *" value={title} onChange={e => setTitle(e.target.value)} data-testid="featured-title-input" />
        <Input placeholder="Badge text (e.g. HOT, NEW)" value={badgeText} onChange={e => setBadgeText(e.target.value)} data-testid="featured-badge-input" />
      </div>
      <Input placeholder="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} data-testid="featured-desc-input" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input placeholder="Thumbnail URL (auto-fills if content selected)" value={thumbnailUrl} onChange={e => setThumbnailUrl(e.target.value)} data-testid="featured-thumb-input" />
        <Input placeholder="CTA text (e.g. Listen Now)" value={ctaText} onChange={e => setCtaText(e.target.value)} data-testid="featured-cta-input" />
      </div>
      {typeConf.searchType === 'manual' && (
        <Input placeholder="External URL (optional)" value={externalUrl} onChange={e => setExternalUrl(e.target.value)} data-testid="featured-url-input" />
      )}

      <div className="flex gap-2 justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={handleSubmit} disabled={submitting || !title.trim()} data-testid={`submit-${typeConf.value}`}>
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          {submitting ? 'Saving...' : 'Set as Featured'}
        </Button>
      </div>
    </div>
  );
}

function ContentSearch({ searchType, getAuthHeaders, selected, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        let url;
        if (searchType === 'track') url = `${API}/search/tracks?q=${encodeURIComponent(query)}&limit=8`;
        else if (searchType === 'video') url = `${API}/search/videos?q=${encodeURIComponent(query)}&limit=8`;
        else url = `${API}/search/users?q=${encodeURIComponent(query)}&limit=8`;

        const res = await fetch(url, { headers: searchType === 'user' ? getAuthHeaders() : {} });
        if (res.ok) {
          const data = await res.json();
          setResults(data.tracks || data.videos || data.users || []);
        }
      } catch (e) { console.error(e); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, searchType, getAuthHeaders]);

  if (selected) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5">
        {(selected.cover_art_url || selected.thumbnail_url || selected.avatar_url || selected.picture) && (
          <img src={selected.cover_art_url || selected.cover_url || selected.thumbnail_url || selected.avatar_url || selected.picture} alt="" className="w-10 h-10 rounded object-cover" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{selected.title || selected.name || selected.stage_name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {searchType === 'track' && `by ${selected.artist_name || 'Unknown'}`}
            {searchType === 'video' && `by ${selected.creator_name || selected.artist_name || 'Unknown'}`}
            {searchType === 'user' && `@${selected.handle || ''} · ${selected.role || ''}`}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onSelect(null)} data-testid="clear-content-selection">
          <XCircle className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        placeholder={`Search ${searchType === 'track' ? 'tracks' : searchType === 'video' ? 'videos' : 'users/creators'}...`}
        value={query}
        onChange={e => setQuery(e.target.value)}
        className="pl-9"
        data-testid="content-search-input"
      />
      {(results.length > 0 || searching) && query.length >= 2 && (
        <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-card border rounded-lg shadow-xl max-h-56 overflow-y-auto">
          {searching ? (
            <div className="p-3 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Searching...</div>
          ) : results.map((item, idx) => {
            const key = item.track_id || item.video_id || item.user_id || idx;
            const thumb = item.cover_art_url || item.cover_url || item.thumbnail_url || item.avatar_url || item.picture;
            const label = item.title || item.name || item.stage_name || 'Untitled';
            const sub = searchType === 'track'
              ? `by ${item.artist_name || 'Unknown'} · ${item.plays || 0} plays`
              : searchType === 'video'
              ? `by ${item.creator_name || item.artist_name || 'Unknown'} · ${item.views || 0} views`
              : `@${item.handle || ''} · ${item.role || ''}`;

            return (
              <button
                key={key}
                onClick={() => { onSelect(item); setQuery(''); setResults([]); }}
                className="w-full flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors text-left"
                data-testid={`search-result-${key}`}
              >
                <div className="w-9 h-9 rounded bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                  {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : <Star className="w-4 h-4 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{label}</p>
                  <p className="text-xs text-muted-foreground truncate">{sub}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
